import React, { Component, RefObject } from 'react';
import classNames from 'classnames';
import styles from './article-tile.scss';
import TileDetails from './details/tileDetails';
import TileVariants from './variants/tileVariants';
import SlideShow from './slideShow/slideShow';
import ButtonColumn from './buttonColumn/buttonColumn';
import { IArticleTileProps, ISdvColor, ISize } from './article-tile.d';
import AvailableSizes from './availableSizes/availableSizes';
import EyeCatcher from './eyeCatcher/eyeCatcher';
import { PageViewLogDataId, SearchTelemetryAjaxCaller, SearchTelemetryData }
    from '../../searchresultpage/SearchTelemetryAjaxCaller';
import AvailableSizesBtn from './availableSizes/availableSizesBtn';
import { ViewType } from '../../../Common/enums';
import TabNavigationHelper from '../../../Common/tabNavigationHelper';

declare global {
    // AB1 => remove delay before variants and slide show activate
    // AB2 => same as AB1 + slide show skips image 1
    interface Window { abTastyEspp12123?: 'AB1' | 'AB2'; }
}

export default class ArticleTile extends Component<IArticleTileProps, {
    isHovering: boolean,
    selectedColorVariant: ISdvColor,
    pdpLink: string,
    showAvailableSizesMenu: boolean,
    sizes: ISize[],
    sizesLoaded: boolean,
    isTouchAction: boolean,
    itWasTouchMove: boolean,
    isDesktop: boolean,
    availableSizesEnabled: boolean,
    mounted: boolean
}> {
    private breakpoint_tablet_max_width_1199 = 1199; //source defaultStyles.breakpoint_tablet_max_width_1199;
    private hoverStartupTimer: ReturnType<typeof setTimeout>;
    private elementRef: RefObject<HTMLDivElement>;
    private tabNav: TabNavigationHelper;

    constructor(props) {
        super(props);
        this.state = {
            isHovering: false,
            selectedColorVariant: this.getDefaultColorVariant(),
            pdpLink: this.props.article.pdpLinkDefaultColor,
            showAvailableSizesMenu: false,
            sizes: [],
            sizesLoaded: false,
            isTouchAction: false,
            itWasTouchMove: false,
            isDesktop: !this.props.isMobile,
            availableSizesEnabled: this.props.article.colorsToAvailableSizes &&
                this.props.article.colorsToAvailableSizes.length > 0 &&
                this.props.article.salesArticleVariantColors.some(it => it.isSale === true),
            mounted: false,
        };

        this.tabNav = TabNavigationHelper.instance;

        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.changeColorVariant = this.changeColorVariant.bind(this);
        this.toggleAvailableSizes = this.toggleAvailableSizes.bind(this);
        this.resetTouchEventState = this.resetTouchEventState.bind(this);
        this.handleTileClick = this.handleTileClick.bind(this);
        this.handleOnMouseDown = this.handleOnMouseDown.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);

        const isTeaserView = this.props.viewType === ViewType.TeaserSuggestion;
        const tileId = 'ats-'
            + this.props.article.masterArticleNo.toString()
            + (isTeaserView ? `_${this.props.gridNumber}` : '');
        this.props.article.tileId = tileId;
        // the parent page component will need to have a ref to the DOM for each article
        // to do scroll distance calculations
        this.elementRef = React.createRef<HTMLDivElement>();
        this.props.article.elementRef = this.elementRef;
    }

    componentDidMount(): void {
        if (this.props.article.elementRef.current)
            this.tabNav.setFocusAtt(this.props.article.elementRef.current);


        // for tablet devices:
        this.setState({
            isDesktop: window.innerWidth > this.breakpoint_tablet_max_width_1199,
            mounted: true
        });
    }

    componentDidUpdate(): void {
        // react will reuse a component (based on key in parent) when we get new articles after filtering
        // so articles that were present before filtering already and stay after filtering will
        // receive new props (new but equal ISalesDesignationView from backend),
        // we copy the element ref into it
        if (!this.props.article.elementRef && this.elementRef) {
            this.props.article.elementRef = this.elementRef;
        }
    }

    componentWillUnmount(): void {
        this.setState({
            mounted: false
        });
    }

    private handleTileClick(tileId: string, pageNo: number): void {
        this.props.urlController.updateUrlForPaging(pageNo, tileId);
    }

    private onMouseEnter(): void {
        clearTimeout(this.hoverStartupTimer);

        this.hoverStartupTimer = setTimeout(() => {
            this.startHover();
        }, this.state.mounted && window.abTastyEspp12123 ? 0 : 1000);
    }

    private startHover(): void {
        this.setState({ isHovering: true });
    }

    private onMouseLeave(): void {
        this.restoreAccessability();
        clearTimeout(this.hoverStartupTimer);

        this.setState({
            selectedColorVariant: this.getDefaultColorVariant(),
            isHovering: false,
            showAvailableSizesMenu: false
        });
    }

    private getDefaultColorVariant() {
        const defaults =
            this.props.article.salesArticleVariantColors
                .filter(it => it.color.code === this.props.article.defaultColorCode);
        return defaults.length < 1 ? null : defaults[0];
    }

    private changeColorVariant(colorCode: number) {
        const colorVariants = this.props.article.salesArticleVariantColors
            .filter(it => it.color.code === colorCode);
        let selectedColor: ISdvColor;
        if (colorVariants.length > 0) {
            selectedColor = colorVariants[0];
        } else {
            selectedColor = this.getDefaultColorVariant()
        }

        this.setState({
            selectedColorVariant: selectedColor,
            pdpLink: selectedColor.pdpLink
        });
    }

    private toggleAvailableSizes(e?): void {
        this.preventDefaultEvents(e);

        if (!this.state.isDesktop) {
            const dcvColorCode = this.getDefaultColorVariant() ?
                this.getDefaultColorVariant().color.code : null;

            this.props.showAvailableSizesOverlay(this.props.article.colorsToAvailableSizes, dcvColorCode,
                this.props.article.masterArticleNo, this.props.article.salesArticleVariantColors);
        }
        else
            this.setState({ showAvailableSizesMenu: !this.state.showAvailableSizesMenu });
    }

    private preventDefaultEvents(e?): void {
        if (e) {
            e.preventDefault(); //the ArticleTile has a <a>-tag to link to PDP, we want to avoid navigation here
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            e.nativeEvent.stopPropagation();
        }
    }

    private get multiColorArticle(): boolean {
        return this.props.article.salesArticleVariantColors && this.props.article.salesArticleVariantColors.length > 1;
    }

    private get detailsAndVariantsSwitchAllowed(): boolean {
        return this.multiColorArticle || this.state.availableSizesEnabled;
    }

    private openHoverContentByTouch(): void {
        if (this.props.isMobile && !this.state.isDesktop)
            return;

        if (this.state.isHovering && !this.state.itWasTouchMove)
            window.location.href = this.state.pdpLink;
        else if (!this.state.itWasTouchMove)
            this.startHover();

        // if it was not movement, on next click pdp will be opened
        this.setState({ itWasTouchMove: false });
    }

    private getPdpLink(): string {
        const pdpLink = this.props.addItemOriginToPdpLink ? this.state.pdpLink + '?itemorigin=SEARCH' : this.state.pdpLink;
        if (this.props.isMobile || !this.state.isTouchAction)
            return pdpLink;
    }

    private resetTouchEventState(event, stm): void {
        // from mobile up to regular tablet, we should keep regular click event
        // feature. But for Kiosk, and High res., tablets, we need first show 
        // hover effect (variants and available sizes) and only on second click
        // proceed with regular event
        if (this.state.isTouchAction && this.state.isDesktop) {
            event.preventDefault();
        }

        this.setState({ isTouchAction: false });
        this.handleTelemetry(stm, this.props.portal, this.props.culture, this.props.magicLink);
    }

    private handleTelemetry(stm: string, portal: string, culture: string, magicLink: string): void {
        if (!stm) return;
        const data = new SearchTelemetryData(
            stm,
            PageViewLogDataId.getInstance().get(),
            portal,
            culture,
            magicLink);
        SearchTelemetryAjaxCaller.postData(data);
    }

    // prevent image from drag, used it ArticleTile List fragment, but does not harm regular features too:
    private handleOnMouseDown(event): void {
        event.preventDefault();
    }

    private handleKeyDown(event): void {
        // prevent page jump down when 3dG is closed with Space btn., click
        if (event.key === ' ') { // space btn
            event.preventDefault();
            // event.target is article tile root element article_tile
            this.tabNav.removeFocusAtt(event.target);
            this.tabNav.setGroupAtt(event.target);
            this.tabNav.setModalAtt(event.target);

            const fas_slides = event.target.querySelector('.fas_slides');
            this.tabNav.setFocusAtt(fas_slides);
            window.shell.tabNav.focus(fas_slides);
            this.startHover();
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.onMouseLeave();
            window.shell.tabNav.focus(this.props.article.elementRef.current, 'keyboard');
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            window.location.href = this.getPdpLink();
        }

        if (!window.shell.tabNav)
            return;

        if (event.key === 'ArrowRight' && event.target.nextSibling) {
            window.shell.tabNav.focus(event.target.nextSibling);
        }

        if (event.key === 'ArrowLeft' && event.target.previousSibling) {
            window.shell.tabNav.focus(event.target.previousSibling);
        }
    }

    private restoreAccessability(): void {
        if (!this.props.article.elementRef.current)
            return; // exit if source element is not available

        const idv_container = this.props.article.elementRef
            .current.querySelector('.fas_idv_container');

        // event.target is fas_idv_container
        this.tabNav.removeFocusAtt(idv_container);

        this.tabNav.removeGroupAtt(this.props.article.elementRef.current);
        this.tabNav.setFocusAtt(this.props.article.elementRef.current);
    }

    public render() {
        const numberOfColorsForAvailableSizes = this.state.availableSizesEnabled
            ? this.props.article.salesArticleVariantColors.length
            : 0;
        const hideDetailsForHover = this.detailsAndVariantsSwitchAllowed && this.state.isHovering;
        const stm = this.props.article.telemetryData ? this.props.article.telemetryData.stm : null;

        return (
            <div id={this.props.article.tileId}
                ref={this.props.article.elementRef}
                className={styles.article_tile}
                data-testid='article_tile'
                onClick={() => { this.handleTileClick(this.props.article.tileId, this.props.article.pageNo) }}
                onMouseEnter={this.props.isMobile || !this.state.isDesktop ? () => { return false; } : this.onMouseEnter}
                onMouseLeave={this.props.isMobile && !this.state.isDesktop ? () => { return false; } : this.onMouseLeave}
                onMouseDown={this.state.isDesktop ? this.handleOnMouseDown : undefined}
                data-stm={stm}
                onKeyDown={this.handleKeyDown}
            >
                <a className='hidden return-anchor' href={'#' + this.props.article.tileId}
                    aria-label={'return anchor for article ' + this.props.article.tileId} />
                {this.props.urlController && <ButtonColumn variant={this.state.selectedColorVariant}
                    seoSlug={this.props.article.seoSlug} navigationKey={this.props.article.navigationKey}
                    on_hover={this.state.isHovering} id={this.props.article.defaultSalesArticleNo}
                    viewType={this.props.viewType} gridNumber={this.props.gridNumber}
                    IsGlobal={this.props.isGlobal}
                    mpcMobileTouchPointsVisible={this.props.mpcMobileTouchPointsVisible}
                    categoryName={this.props.category.categoryName}
                    bookmarkMlt={this.props.l10n.bookmark} />
                }
                <a href={this.getPdpLink()}
                    className={classNames(styles.idv_container, {
                        [styles.filter_drop_down_open]: this.props.isFilterDropDownOpen
                    })}
                    data-testid='idv_container'
                    onTouchMove={() => {
                        this.setState({ itWasTouchMove: true });
                    }}
                    onTouchEnd={() => this.openHoverContentByTouch()}
                    onTouchStart={() => {
                        this.setState({ isTouchAction: true });
                    }}
                    onClick={(event) => this.resetTouchEventState(event, stm)}
                >
                    <div className={styles.media_container}>

                        <SlideShow
                            sdvColor={this.state.selectedColorVariant}
                            on_hover={this.state.isHovering}
                            detailsPageLink={this.props.article.pdpLinkDefaultColor}
                            tileId={this.props.article.defaultSalesArticleNo}
                            showAvailableSizesMenu={this.state.showAvailableSizesMenu}
                            articleName={this.props.article.description}
                            preventDefaultEvents={this.preventDefaultEvents}
                            singleArticleView={this.props.singleArticleView}
                            colorName={this.state.selectedColorVariant.color.name}
                        />

                        <div className={styles.tile_extras}>
                            <EyeCatcher
                                isNew={this.state.selectedColorVariant.isNew}
                                hasUvp={this.state.selectedColorVariant.hasRecommendedRetailPrice}
                                sale={this.state.selectedColorVariant.isSale}
                                priceDeduction={this.props.article.priceDeduction}
                                setPrice={this.props.article.salesArticleType === 2}  // ArticleSet type
                                availableSizesOpen={this.state.showAvailableSizesMenu}
                                numberOfColorsForAvailableSizes={numberOfColorsForAvailableSizes}
                                flagAdvertisedAsCostFreeInSet={this.props.article.flagAdvertisedAsCostFreeInSet}
                            />
                            {
                                (!this.state.isDesktop && this.state.availableSizesEnabled) &&
                                <AvailableSizesBtn toggleAvailableSizes={this.toggleAvailableSizes}
                                    btnText={this.props.l10n.availableSizes} />
                            }
                        </div>
                    </div>
                    <div className={styles.dv_container}>
                        <TileDetails
                            currencyInformation={this.props.currencyInformation}
                            priceType={this.props.article.priceType}
                            minimalPrice={this.props.article.minimalPrice}
                            description={this.props.article.description}
                            variantCountInfo={this.props.article.variantCountInfo}
                            hideSelfForHover={hideDetailsForHover}
                            originalPrice={this.props.article.originalPrice}
                            comparisonPrice={this.props.article.comparisonPrice}
                            isMobile={this.props.isMobile}
                            isGlobal={this.props.isGlobal}
                            flagAdvertisedAsCostFreeInSet={this.props.article.flagAdvertisedAsCostFreeInSet}
                            packagingInformation={this.props.article.packagingInformation}
                        />
                        {(this.detailsAndVariantsSwitchAllowed && this.state.isDesktop) &&
                            <TileVariants
                                article={this.props.article}
                                tileIsHovering={this.state.isHovering}
                                showAvailableSizesButton={this.state.availableSizesEnabled}
                                changeColorVariant={this.changeColorVariant}
                                selectedColor={this.state.selectedColorVariant.color.code}
                                toggleAvailableSizes={this.toggleAvailableSizes}
                                showAvailableSizesMenu={this.state.showAvailableSizesMenu}
                            />
                        }
                    </div>
                </a>
                {(this.state.availableSizesEnabled && this.state.isDesktop) &&
                    <AvailableSizes salesArticleVariantColors={this.props.article.salesArticleVariantColors}
                        dcvColorCode={this.getDefaultColorVariant() ? this.getDefaultColorVariant().color.code : null}
                        toggleAvailableSizes={this.toggleAvailableSizes}
                        showAvailableSizesMenu={this.state.showAvailableSizesMenu}
                        colorsToAvailableSizes={this.props.article.colorsToAvailableSizes}
                        masterArticleNo={this.props.article.masterArticleNo}
                    />}
            </div>
        );
    }
}
